'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import {
  JobResponse,
  HistoryItem,
  QueueStats,
  HealthResponse,
  Series,
  VariantSession,
  VariantSessionCreate,
  VariantGenerateFullRequest,
} from '@/lib/types';
import { useToast } from '@/components/Toast';
import { useConfirm } from '@/components/ConfirmDialog';
import JobCard from '@/components/JobCard';
import HistoryPanel from '@/components/HistoryPanel';
import StatusBar from '@/components/StatusBar';
import ImageModal from '@/components/ImageModal';
import SeriesPanel from '@/components/SeriesPanel';
import VariantGenerationForm from '@/components/variants/VariantGenerationForm';
import VariantPreviewGrid from '@/components/variants/VariantPreviewGrid';

export default function VariantsPage() {
  const [jobs, setJobs] = useState<JobResponse[]>([]);
  const jobsRef = useRef<JobResponse[]>([]);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [selectedJob, setSelectedJob] = useState<JobResponse | null>(null);
  const [series, setSeries] = useState<Series[]>([]);
  const [selectedSeriesId, setSelectedSeriesId] = useState<number | null>(null);
  const toast = useToast();
  const { confirm } = useConfirm();

  // Variant-specific state
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentSession, setCurrentSession] = useState<VariantSession | null>(null);
  const [selectedPromptIds, setSelectedPromptIds] = useState<Set<number>>(new Set());
  const [isPollingPreviews, setIsPollingPreviews] = useState(false);
  const [isGeneratingFull, setIsGeneratingFull] = useState(false);

  // Generation settings for full quality
  const [fullWidth, setFullWidth] = useState(1024);
  const [fullHeight, setFullHeight] = useState(1024);
  const [fullSteps, setFullSteps] = useState(9);
  const [showFullSettings, setShowFullSettings] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [healthRes, statsRes, jobsRes, historyRes, seriesRes] = await Promise.all([
        api.getHealth(),
        api.getQueueStats(),
        api.listJobs(undefined, 500, 0, selectedSeriesId ?? undefined),
        api.getHistory(50),
        api.getSeries(),
      ]);
      setHealth(healthRes);
      setStats(statsRes);
      setJobs(jobsRes);
      jobsRef.current = jobsRes;
      setHistory(historyRes);
      setSeries(seriesRes);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, [selectedSeriesId]);

  // Initial data fetch and polling
  useEffect(() => {
    fetchData();
    const interval = setInterval(() => {
      const hasPendingOrProcessing = jobsRef.current.some(
        (j) => j.status === 'pending' || j.status === 'processing'
      );
      if (hasPendingOrProcessing) {
        fetchData();
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Poll for preview status
  useEffect(() => {
    if (!currentSession || !isPollingPreviews) return;

    const pollInterval = setInterval(async () => {
      try {
        const status = await api.getVariantPreviewStatus(currentSession.id);
        setCurrentSession((prev) =>
          prev ? { ...prev, prompts: status.prompts } : null
        );

        if (status.is_complete) {
          setIsPollingPreviews(false);
          toast.success('All preview images generated');
        }
      } catch (error) {
        console.error('Failed to poll preview status:', error);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [currentSession?.id, isPollingPreviews, toast]);

  // Handle variant generation
  const handleGenerateVariants = async (request: VariantSessionCreate) => {
    setIsGenerating(true);
    setSelectedPromptIds(new Set());
    try {
      const response = await api.generateVariantPrompts(request);
      // Fetch the full session with prompts
      const session = await api.getVariantSession(response.session_id);
      setCurrentSession(session);
      setIsPollingPreviews(true);
      toast.success(`Generated ${response.prompts.length} prompt variations`);
    } catch (error) {
      console.error('Failed to generate variants:', error);
      toast.error('Failed to generate variants: ' + (error as Error).message);
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle full quality generation
  const handleGenerateFull = async () => {
    if (!currentSession || selectedPromptIds.size === 0) return;

    const confirmed = await confirm({
      title: 'Generate Full Quality Images',
      message: `This will create ${selectedPromptIds.size} full-quality image${selectedPromptIds.size !== 1 ? 's' : ''} at ${fullWidth}x${fullHeight}. Continue?`,
      confirmText: 'Generate',
      variant: 'default',
    });

    if (!confirmed) return;

    setIsGeneratingFull(true);
    try {
      const request: VariantGenerateFullRequest = {
        variant_ids: Array.from(selectedPromptIds),
        width: fullWidth,
        height: fullHeight,
        steps: fullSteps,
        series_id: selectedSeriesId,
      };
      const response = await api.generateVariantFull(currentSession.id, request);
      toast.success(`Created ${response.total_jobs} job${response.total_jobs !== 1 ? 's' : ''}`);
      setSelectedPromptIds(new Set());
      await fetchData();
    } catch (error) {
      console.error('Failed to generate full images:', error);
      toast.error('Failed to generate: ' + (error as Error).message);
    } finally {
      setIsGeneratingFull(false);
    }
  };

  // Selection handlers
  const handleToggleSelect = (promptId: number) => {
    setSelectedPromptIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(promptId)) {
        newSet.delete(promptId);
      } else {
        newSet.add(promptId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (!currentSession) return;
    const completedIds = currentSession.prompts
      .filter((p) => p.preview_status === 'completed')
      .map((p) => p.id);
    setSelectedPromptIds(new Set(completedIds));
  };

  const handleDeselectAll = () => {
    setSelectedPromptIds(new Set());
  };

  // Job handlers
  const handleDeleteJob = async (jobId: string) => {
    try {
      await api.deleteJobPermanent(jobId);
      await fetchData();
    } catch (error) {
      console.error('Failed to delete job:', error);
    }
  };

  const handleDeleteHistoryItem = async (itemId: number) => {
    try {
      await api.deleteHistoryItem(itemId);
      await fetchData();
    } catch (error) {
      console.error('Failed to delete history item:', error);
    }
  };

  const handleClearHistory = async (period?: 'today' | 'yesterday' | 'all') => {
    const periodLabels = {
      today: "today's",
      yesterday: "yesterday's",
      all: 'all',
    };
    const label = periodLabels[period || 'all'];

    const confirmed = await confirm({
      title: 'Clear History',
      message: `This will permanently delete ${label} history items.`,
      confirmText: period === 'all' ? 'Clear All' : `Clear ${label.charAt(0).toUpperCase() + label.slice(1)}`,
      variant: 'danger',
    });

    if (confirmed) {
      try {
        const result = await api.clearHistoryByPeriod(period || 'all');
        await fetchData();
        toast.success(`Cleared ${result.cleared} history items`);
      } catch (error) {
        console.error('Failed to clear history:', error);
        toast.error('Failed to clear history');
      }
    }
  };

  const activeJobs = jobs.filter((j) => j.status === 'pending' || j.status === 'processing');
  const completedJobs = jobs.filter((j) => j.status === 'completed');
  const totalImages =
    series.reduce((sum, s) => sum + s.image_count, 0) +
    jobs.filter((j) => j.status === 'completed' && !j.series_id).length;

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <StatusBar health={health} stats={stats} onRefresh={fetchData} />

      <div className="flex-1 flex overflow-hidden">
        {/* Series Sidebar */}
        <aside className="hidden lg:block w-64 border-r border-[var(--border-subtle)] flex-shrink-0 overflow-y-auto">
          <SeriesPanel
            series={series}
            selectedSeriesId={selectedSeriesId}
            totalImages={totalImages}
            onSelectSeries={setSelectedSeriesId}
            onSeriesChange={fetchData}
          />
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0 overflow-y-auto">
          <div className="max-w-6xl mx-auto px-6 py-8 space-y-10">
            {/* Header */}
            <div className="space-y-2">
              <h1 className="text-3xl font-semibold tracking-tight">Variants</h1>
              <p className="text-[var(--text-muted)]">
                Generate AI-powered prompt variations with instant previews
              </p>
            </div>

            {/* Generation Form - always visible */}
            <VariantGenerationForm
              onGenerate={handleGenerateVariants}
              isLoading={isGenerating}
              series={series}
              selectedSeriesId={selectedSeriesId}
            />

            {/* Preview Grid - show when session exists */}
            {currentSession && (
              <div className="space-y-6">
                {/* Preview Grid Header */}
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Preview Variants
                  </h2>
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                      currentSession.variation_style === 'refine'
                        ? 'bg-blue-500/20 text-blue-400'
                        : currentSession.variation_style === 'explore'
                        ? 'bg-purple-500/20 text-purple-400'
                        : 'bg-orange-500/20 text-orange-400'
                    }`}
                  >
                    {currentSession.variation_style.charAt(0).toUpperCase() +
                      currentSession.variation_style.slice(1)}
                  </span>
                </div>

                {/* Preview Grid */}
                <VariantPreviewGrid
                  prompts={currentSession.prompts || []}
                  selectedIds={selectedPromptIds}
                  onToggleSelect={handleToggleSelect}
                  onSelectAll={handleSelectAll}
                  onDeselectAll={handleDeselectAll}
                  isPolling={isPollingPreviews}
                  seriesId={selectedSeriesId}
                />

                {/* Full Quality Generation Controls */}
                {selectedPromptIds.size > 0 && (
                  <div className="sticky bottom-0 p-4 -mx-6 px-6 bg-[var(--bg-primary)] border-t border-[var(--border-subtle)]">
                    {/* Series warning */}
                    {!selectedSeriesId && (
                      <div className="flex items-center gap-2 mb-3 p-2 rounded-lg bg-[rgba(251,191,36,0.1)] border border-[#fbbf24] text-sm text-[#fbbf24]">
                        <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                        </svg>
                        <span>No series selected - images will go to &quot;All Images&quot;</span>
                      </div>
                    )}
                    {selectedSeriesId && (
                      <div className="flex items-center gap-2 mb-3 text-sm text-[var(--text-muted)]">
                        <span>Images will be added to:</span>
                        <span className="font-medium text-[var(--text-secondary)]">
                          {series.find(s => s.id === selectedSeriesId)?.name || 'Unknown Series'}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-[var(--text-muted)]">
                          {selectedPromptIds.size} selected
                        </span>

                        {/* Settings toggle */}
                        <button
                          type="button"
                          onClick={() => setShowFullSettings(!showFullSettings)}
                          className="text-sm text-[var(--accent)] hover:underline cursor-pointer"
                        >
                          {showFullSettings ? 'Hide Settings' : 'Settings'}
                        </button>
                      </div>

                      <button
                        type="button"
                        onClick={handleGenerateFull}
                        disabled={isGeneratingFull}
                        className="btn btn-primary"
                      >
                        {isGeneratingFull ? (
                          <span className="flex items-center gap-2">
                            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            Creating...
                          </span>
                        ) : (
                          `Generate ${selectedPromptIds.size} Full Quality`
                        )}
                      </button>
                    </div>

                    {/* Expandable settings */}
                    {showFullSettings && (
                      <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-[var(--border-subtle)]">
                        <div className="space-y-2">
                          <label className="flex items-center justify-between text-xs">
                            <span className="text-[var(--text-muted)]">Width</span>
                            <span className="text-[var(--text-primary)]">{fullWidth}</span>
                          </label>
                          <input
                            type="range"
                            value={fullWidth}
                            onChange={(e) => setFullWidth(parseInt(e.target.value))}
                            min={512}
                            max={2048}
                            step={64}
                            className="w-full accent-[var(--accent)] cursor-pointer"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="flex items-center justify-between text-xs">
                            <span className="text-[var(--text-muted)]">Height</span>
                            <span className="text-[var(--text-primary)]">{fullHeight}</span>
                          </label>
                          <input
                            type="range"
                            value={fullHeight}
                            onChange={(e) => setFullHeight(parseInt(e.target.value))}
                            min={512}
                            max={2048}
                            step={64}
                            className="w-full accent-[var(--accent)] cursor-pointer"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="flex items-center justify-between text-xs">
                            <span className="text-[var(--text-muted)]">Steps</span>
                            <span className="text-[var(--text-primary)]">{fullSteps}</span>
                          </label>
                          <input
                            type="range"
                            value={fullSteps}
                            onChange={(e) => setFullSteps(parseInt(e.target.value))}
                            min={1}
                            max={50}
                            className="w-full accent-[var(--accent)] cursor-pointer"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Active Jobs */}
            {activeJobs.length > 0 && (
              <section className="space-y-4">
                <h2 className="text-sm font-medium text-[var(--text-muted)] uppercase tracking-wider">
                  In Progress
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {activeJobs.map((job) => (
                    <JobCard key={job.job_id} job={job} onSelect={setSelectedJob} />
                  ))}
                </div>
              </section>
            )}

            {/* Gallery */}
            <section className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-medium text-[var(--text-muted)] uppercase tracking-wider">
                  Gallery
                </h2>
                {completedJobs.length > 0 && (
                  <span className="text-xs text-[var(--text-muted)]">
                    {completedJobs.length} images
                  </span>
                )}
              </div>

              {completedJobs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 rounded-full bg-[var(--bg-elevated)] flex items-center justify-center mb-4">
                    <svg
                      className="w-8 h-8 text-[var(--text-muted)]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={1}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                      />
                    </svg>
                  </div>
                  <p className="text-[var(--text-muted)]">No images yet</p>
                  <p className="text-sm text-[var(--text-muted)] mt-1">
                    Generate variants and select your favorites
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {completedJobs.map((job) => (
                    <JobCard
                      key={job.job_id}
                      job={job}
                      onSelect={setSelectedJob}
                      onDelete={handleDeleteJob}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>
        </main>

        {/* History Sidebar */}
        <aside className="hidden lg:block w-72 border-l border-[var(--border-subtle)] flex-shrink-0 overflow-hidden">
          <HistoryPanel
            history={history}
            onReuseParams={() => {}}
            onDeleteItem={handleDeleteHistoryItem}
            onClearHistory={handleClearHistory}
          />
        </aside>
      </div>

      {/* Image Modal */}
      {selectedJob && selectedJob.status === 'completed' && (
        <ImageModal
          job={selectedJob}
          jobs={completedJobs}
          onClose={() => setSelectedJob(null)}
          onReuseParams={() => {}}
          onDelete={handleDeleteJob}
          onNavigate={setSelectedJob}
          series={series}
          onSeriesChange={fetchData}
        />
      )}
    </div>
  );
}
